import { Injectable, inject } from "@angular/core";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { BaseLoginInfo } from "./user.service";
import { LoggerService } from "src/ancestors/logger.service";

/** Attributi del cookie che porta le informazioni di login, ripetuti identici in scrittura e in cancellazione. */
const COOKIE_SECURITY_ATTRIBUTES = "samesite=strict; path=/; secure";

/**
 * Legge il valore di un cookie dato il suo nome, senza interpretarlo.
 * @param name Nome del cookie
 * @returns Il valore grezzo, oppure `undefined` se il cookie non c'è
 */
function readRawCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const entry = document.cookie.split(";").find(item => item.trim().startsWith(prefix));
  return entry ? entry.trim().substring(prefix.length) : undefined;
}

/**
 * Codifica per un valore di cookie, limitata ai caratteri che lo rompono davvero.
 *
 * `encodeURIComponent` sull'intero valore sembra la scelta ovvia e **non lo è**: qui il valore è un
 * JSON, quindi è fatto in gran parte di `"` `{` `}` `:` `,`, che quella funzione percent-codifica.
 * La stringa cresce di circa il 25%, e il payload di un SUPER_ADMIN è a ~4000 byte contro un limite
 * di **4096 byte per cookie**: la codifica integrale lo portava a ~5000 byte e il browser scartava
 * il cookie **in silenzio**. Il login funzionava, il token non veniva conservato, e la guard di
 * rotta riportava l'utente alla pagina di accesso senza un errore da nessuna parte.
 *
 * Si codifica quindi solo ciò che serve:
 * - `;` è il separatore degli attributi del cookie: dentro un valore lo troncherebbe e, in teoria,
 *   permetterebbe di iniettare un attributo. È il caso di un `;` in un cognome o in un nome di
 *   organizzazione, cioè il difetto per cui questa codifica esiste;
 * - i caratteri di controllo (una nuova riga incollata in un campo) terminano il valore;
 * - `%` va codificato anch'esso, altrimenti la decodifica sarebbe ambigua.
 *
 * `"` `,` `{` `}` `:` e **lo spazio** restano invariati: i browser li conservano in
 * `document.cookie`, ed è quello che questo cookie ha sempre fatto — un cognome ha uno spazio, e
 * codificarlo costerebbe byte proprio dove non ce ne sono. Sui dati reali questa codifica non
 * aggiunge quindi nemmeno un byte: interviene solo sui caratteri che oggi romperebbero il cookie.
 * @param value Valore da mettere nel cookie
 * @returns Il valore con i soli caratteri problematici percent-codificati
 */
function encodeCookieValue(value: string): string {
  let encoded = "";
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    // caratteri di controllo inclusi, spazio escluso; i caratteri accentati (code > 0x7F) restano
    // invariati, come sono sempre stati in questo cookie
    if (character === "%" || character === ";" || code < 0x20 || code === 0x7F) {
      encoded += `%${code.toString(16).toUpperCase().padStart(2, "0")}`;
    } else {
      encoded += character;
    }
  }
  return encoded;
}

/**
 * Decodifica il valore di un cookie tollerando i cookie scritti dalle versioni precedenti.
 *
 * Il ripiego non è un dettaglio: `decodeURIComponent` **lancia** su una stringa con un `%` non
 * seguito da due cifre esadecimali, ed è ciò che evita di sbattere fuori chiunque abbia una
 * sessione aperta al momento del rilascio.
 * @param value Valore grezzo del cookie
 * @returns Il valore decodificato, o quello originale se non è codificato
 */
function decodeCookieValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Cosa viene DAVVERO conservato nel browser, campo per campo.
 *
 * È una whitelist espressa come tipo, e la forma non è casuale: ogni campo è dichiarato come
 * `BaseLoginInfo[...]`, quindi se il contratto con il backend cambia nome a un campo questo file
 * non compila piu' invece di conservare in silenzio un dato che non esiste.
 *
 * Cosa NON entra, perché nessuna riga del frontend lo legge (verificato con grep su `.ts` e
 * `.html`): `jwtPayload.user.status`, `.tenantId`, `.tenantHost`, e i claim standard del JWT
 * (`exp`, `iat`, `iss`, `sub`). Il guadagno diretto è modesto — ~90 byte — ma il punto è un altro:
 * un campo che il backend aggiungerà in futuro non finirà nello storage per inerzia.
 */
interface StoredLoginInfo {
  expireDate: BaseLoginInfo["expireDate"];
  /**
   * Presente SOLO se il backend lo ha valorizzato, e la differenza è tutt'altro che formale:
   * `isLoginInfo` (`base-api.service.ts`) considera "informazioni di login" un oggetto che ha la
   * chiave `token`, e in quel caso aggiunge l'header `Authorization: Bearer <valore>`. Quell'header
   * **vince sul cookie di sessione** (`sharedutils`, `decorators/auth.ts`), quindi una chiave
   * presente e vuota farebbe rispondere 403 a ogni chiamata. In modalità cookie il backend non lo
   * ritorna affatto, ed è giusto che qui non compaia.
   */
  token?: BaseLoginInfo["token"];
  userContextType?: BaseLoginInfo["userContextType"];
  organizationId?: BaseLoginInfo["organizationId"];
  jwtPayload: {
    auths: BaseLoginInfo["jwtPayload"]["auths"];
    profiles?: BaseLoginInfo["jwtPayload"]["profiles"];
    /** Serve al solo token MFA-pending: `hasMFALoginToDo` legge `params._mfaContext` */
    params?: BaseLoginInfo["jwtPayload"]["params"];
    user: Pick<BaseLoginInfo["jwtPayload"]["user"], "userId" | "email" | "forename" | "surname" | "locale">;
  };
}

/**
 * Riduce le informazioni di login a ciò che il frontend legge davvero.
 * @param loginInfo Risposta di login, refresh o cambio tenant
 * @returns L'oggetto da serializzare nello storage
 */
function projectForStorage(loginInfo: BaseLoginInfo): StoredLoginInfo {
  const jwtPayload = loginInfo?.jwtPayload;
  const user = jwtPayload?.user;
  const projected: StoredLoginInfo = {
    expireDate: loginInfo?.expireDate,
    userContextType: loginInfo?.userContextType,
    organizationId: loginInfo?.organizationId,
    jwtPayload: {
      auths: jwtPayload?.auths,
      profiles: jwtPayload?.profiles,
      params: jwtPayload?.params,
      user: {
        userId: user?.userId,
        email: user?.email,
        forename: user?.forename,
        surname: user?.surname,
        locale: user?.locale
      }
    }
  };
  // Vedi il commento su `StoredLoginInfo.token`: la chiave entra solo se c'è un valore.
  if (loginInfo?.token) {
    projected.token = loginInfo.token;
  }
  return projected;
}

@Injectable()
export class TokenStorageService {

  private env = inject(EnvironmentLoaderService);
  private logger = inject(LoggerService);

  constructor() { }

  /**
   * 
   * @returns token salvato + data scadenza
   */
  public retriveToken(tokenKey: string): BaseLoginInfo {
    const storage = this.resolveWebStorage();
    let payload: string | null | undefined;

    if (storage) {
      payload = storage.getItem(tokenKey) ?? this.adoptLegacyCookie(tokenKey, storage);
    } else {
      const rawCookie = readRawCookie(tokenKey);
      payload = rawCookie === undefined ? undefined : decodeCookieValue(rawCookie);
    }

    if (payload) {
      try {
        return JSON.parse(payload) as BaseLoginInfo;
      } catch {
        // Un valore illeggibile equivale a nessuna sessione: la si tratta come assente invece di
        // lasciar propagare l'eccezione, perché questa funzione sta sul percorso di ogni chiamata
        // HTTP e di ogni guard di rotta.
        return {} as BaseLoginInfo;
      }
    }

    return {} as BaseLoginInfo;

  }
  /**
   * @param token
   * Salva il token nello storage
   */
  public saveToken(tokenKey: string, token: BaseLoginInfo): void {
    const payload = JSON.stringify(projectForStorage(token));
    const storage = this.resolveWebStorage();

    if (storage) {
      try {
        storage.setItem(tokenKey, payload);
      } catch (error: unknown) {
        // Safari in navigazione privata rifiuta `setItem`. Come per il cookie scartato, il sintomo
        // che arriva all'utente è "resta sulla pagina di login": va detto dove guardare.
        this.logger.error(
          `Non è stato possibile conservare la sessione (${payload.length} byte) nello storage del browser: `
          + (error instanceof Error ? error.message : String(error))
        );
      }
      return;
    }

    // Set cookie expiration to match token expiration date to persist across browser sessions
    const expirationDate = new Date(token.expireDate);
    const expires = expirationDate.toUTCString();
    // Due cose che prima mancavano:
    // - `secure`: il cookie non deve viaggiare in chiaro. I due cookie emessi dal backend sono
    //   già `secure`, quindi nessun ambiente funzionante oggi ne risente (e i browser
    //   accettano cookie `Secure` su http://localhost);
    // - la codifica dei caratteri che romperebbero il cookie: un `;` in un campo anagrafico
    //   (cognome, nome dell'organizzazione) lo troncherebbe. Vedi `encodeCookieValue` per il
    //   perché NON si usa `encodeURIComponent`: il valore è a un soffio dal limite di 4096 byte.
    const encodedValue = encodeCookieValue(payload);
    document.cookie = `${tokenKey}=${encodedValue}; expires=${expires}; ${COOKIE_SECURITY_ATTRIBUTES}`;
    this.warnIfCookieWasRejected(tokenKey, encodedValue);
  }

  /**
   * L'oggetto `Storage` della modalità configurata, oppure `null` se la sessione va nel cookie.
   *
   * `localstorage` è la modalità di questo prodotto: il payload di login contiene l'elenco delle
   * AUTH (~3,5 KB da solo) e nel cookie era al 98% del limite di 4096 byte imposto dai browser.
   * `localStorage` ne ammette qualche megabyte, quindi il vincolo non torna quando il catalogo dei
   * permessi si allunga; e come effetto collaterale quei byte smettono di essere allegati
   * all'header `Cookie` di OGNI chiamata all'API.
   * @returns Lo storage da usare, o `null` per la modalità a cookie
   */
  private resolveWebStorage(): Storage | null {
    const { storeAccessToken } = this.env.getEnvConfig();
    switch (storeAccessToken) {
      case "localstorage": {
        return localStorage;
      }
      case "sessionstorage": {
        return sessionStorage;
      }
      default: {
        return null;
      }
    }
  }

  /**
   * Recupera una sessione lasciata nel cookie dalla versione precedente e la sposta nello storage.
   *
   * Serve una volta sola, al primo caricamento dopo il rilascio, e serve per due motivi: senza,
   * tutti gli utenti con una sessione aperta tornerebbero alla pagina di login, **e il cookie
   * sovradimensionato resterebbe nel browser per dieci anni** — la sua scadenza — continuando a
   * viaggiare in ogni richiesta.
   * @param tokenKey Nome della chiave di sessione
   * @param storage Storage di destinazione
   * @returns Il payload recuperato, oppure `null` se non c'era alcun cookie
   */
  private adoptLegacyCookie(tokenKey: string, storage: Storage): string | null {
    const rawCookie = readRawCookie(tokenKey);
    if (rawCookie === undefined) {
      return null;
    }
    const payload = decodeCookieValue(rawCookie);
    try {
      storage.setItem(tokenKey, payload);
      this.clearCookie(tokenKey);
      this.logger.info(`Sessione spostata dal cookie ${tokenKey} allo storage del browser`);
    } catch (error: unknown) {
      // Se lo storage non accetta la scrittura il cookie NON va cancellato: è l'unica copia della
      // sessione, e questo caricamento di pagina deve comunque funzionare.
      this.logger.warn(`Non è stato possibile spostare la sessione dal cookie ${tokenKey} allo storage del browser`);
    }
    return payload;
  }

  /**
   * Segnala se il browser ha rifiutato il cookie appena scritto.
   *
   * Un cookie oltre i 4096 byte viene scartato **in silenzio**: nessuna eccezione, nessun errore, e
   * il sintomo che arriva all'utente ("resta sulla pagina di login") è lontanissimo dalla causa.
   * @param tokenKey Nome del cookie
   * @param writtenValue Valore che si è tentato di scrivere
   */
  private warnIfCookieWasRejected(tokenKey: string, writtenValue: string): void {
    if (readRawCookie(tokenKey) !== undefined) {
      return;
    }
    this.logger.error(
      `Il cookie ${tokenKey} non è stato accettato dal browser: ${tokenKey.length + 1 + writtenValue.length} byte, `
      + "oltre il limite di 4096 per singolo cookie. La sessione non verrà conservata: va usata la "
      + "modalità `localstorage` (`storeAccessToken` nei config sotto assets/config)."
    );
  }

  /** Cancella il cookie di sessione, ripetendo gli attributi della scrittura */
  private clearCookie(tokenKey: string): void {
    // Gli attributi vanno ripetuti identici alla scrittura, altrimenti la sovrascrittura può
    // non combaciare e il cookie sopravvive alla cancellazione.
    document.cookie = `${tokenKey}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${COOKIE_SECURITY_ATTRIBUTES}`;
  }

  /**
   * 
   * Elimina il token dallo storage
   */
  public deleteToken(tokenKey: string): void {
    const storage = this.resolveWebStorage();
    if (storage) {
      storage.removeItem(tokenKey);
    }
    // Il cookie si cancella in ogni caso: in modalità storage può esserci ancora quello lasciato
    // dalla versione precedente, e un logout non deve lasciare residui della sessione.
    this.clearCookie(tokenKey);
  }

}
